import {
  motion,
  useInView,
  type MotionProps,
  type Variants,
} from 'framer-motion';
import { type ElementType, type RefObject, useRef } from 'react';

/**
 * TimelineContent — a tiny intersection-observer-driven reveal wrapper.
 *
 * The original component (from a layout-blocks library) had a more complex
 * timeline-rail concept; for our needs, the useful pattern is "fade-in on
 * scroll, with an `animationNum` index that staggers the delay across
 * sibling elements." That's all this implements.
 *
 * Usage:
 *   <TimelineContent as="h1" animationNum={3} timelineRef={ref}>...</TimelineContent>
 *
 * The `timelineRef` is the SECTION that owns the timeline. We watch when it
 * enters the viewport and replay the staggered sequence each time it does.
 */

export const defaultRevealVariants: Variants = {
  hidden: {
    y: -20,
    opacity: 0,
    filter: 'blur(10px)',
  },
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    filter: 'blur(0px)',
    transition: {
      delay: i * 0.12,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

type TimelineContentProps<T extends ElementType> = {
  as?: T;
  animationNum: number;
  timelineRef: RefObject<HTMLElement | null>;
  customVariants?: Variants;
  children?: React.ReactNode;
  className?: string;
} & Omit<MotionProps, 'variants' | 'initial' | 'animate' | 'custom'>
  & Omit<React.ComponentPropsWithoutRef<T>, 'ref' | keyof MotionProps>;

export function TimelineContent<T extends ElementType = 'div'>({
  as,
  animationNum,
  timelineRef,
  customVariants,
  children,
  className,
  ...rest
}: TimelineContentProps<T>) {
  const fallbackRef = useRef<HTMLElement | null>(null);
  const observedRef = (timelineRef ?? fallbackRef) as RefObject<HTMLElement | null>;

  // `once: false` so the animation replays if the user scrolls back up.
  // amount: 0.2 — fire when 20% of the section is in view, generous on tall heroes.
  const inView = useInView(observedRef, { once: false, amount: 0.2 });

  const Tag = (as ?? 'div') as ElementType;
  const MotionTag = motion(Tag) as typeof motion.div;

  return (
    <MotionTag
      className={className}
      custom={animationNum}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={customVariants ?? defaultRevealVariants}
      {...(rest as Record<string, unknown>)}
    >
      {children}
    </MotionTag>
  );
}
